<template>

  <v-container class="page page-register-shipper-account px-8 pb-8">

      <!----------------------------------------------------------------------->
      <!-- Registration Form -->
      <!----------------------------------------------------------------------->
      <v-form
          v-if="!registeredUser && !isProcessing"
          lazy-validation
          ref="form"
          class="registration-form"
          autocomplete="off"
          v-model="isFormValid">

        <!-- title -->
        <v-row>
          <v-col align="center" class="form-title">
            {{ formTitleText }}
          </v-col>
        </v-row>

        <!-- information area -->
        <v-row class="mt-2 mb-6">
          <v-col align="center">
            <div class="main-text">Your email will be the owner of the account.</div>
            <div class="info-text mt-2">You can invite team members once approved.</div>
          </v-col>
        </v-row>

        <!-- registration code -->
        <v-row class="pb-0" v-if="requireRegistrationCode">
          <v-col>
            <v-text-field
                outlined
                required
                :rules="$root.rules.fieldRequired"
                label="Registration code"
                v-model.trim="registrationCode" />
          </v-col>
        </v-row>

        <!-- email -->
        <v-row class="mt-0">
          <v-col class="ma-0 pt-0">
            <v-text-field
                required
                :rules="$root.rules.fieldRequired"
                label="Email"
                autocomplete="new-email"
                v-model.trim="email" />
          </v-col>
        </v-row>

        <!-- password -->
        <v-row class="mt-1">
          <v-col class="mb-0 pb-0">
            <v-text-field
                required
                :rules="$root.rules.fieldRequired"
                label="Password"
                autocomplete="new-password"
                v-model.trim="password"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword" />
          </v-col>
        </v-row>
        <!-- password requirements -->
        <v-row class="pa-0 ma-0 mt-2">
          <v-col class="pa-0 ma-0">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="password-requirements-text">
                  Password requirements
                  <v-icon style="font-size:17px;" color="#34b3db" class="pl-1">mdi-information-outline</v-icon>
                </div>
              </template>
              <div>
                <span>Must be at least 8 characters, and contain:</span><br />
                <ul class="mt-1">
                  <li>1 number</li>
                  <li>1 uppercase letter</li>
                  <li>1 lowercase letter</li>
                  <li>1 special character</li>
                </ul>
              </div>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- org name -->
        <v-row class="mt-4">
          <v-col>
            <v-text-field
                required
                :rules="$root.rules.fieldRequired"
                label="Organization name"
                v-model="orgName" />
          </v-col>
        </v-row>

        <!-- brand name -->
        <v-row class="mt-1">
          <v-col>
            <v-text-field
                label="Brand name(s)"
                v-model="brandNames" />
          </v-col>
        </v-row>

        <!-- first name -->
        <v-row class="mt-1">
          <v-col>
            <v-text-field
                required
                :rules="$root.rules.fieldRequired"
                label="First name"
                v-model="firstName" />
          </v-col>
        </v-row>

        <!-- last name -->
        <v-row class="mt-1">
          <v-col>
            <v-text-field
                required
                :rules="$root.rules.fieldRequired"
                label="Last name"
                v-model="lastName" />
          </v-col>
        </v-row>

        <!-- (registration error display) -->
        <template v-if="activeRegistrationError">
          <transition name="fade" appear>
            <v-row>
              <v-col>
                <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
                <span class="registration-error-message-text">{{ activeRegistrationError }}</span>
              </v-col>
            </v-row>
          </transition>
        </template>

        <!-- register button -->
        <v-row>
          <v-col class="d-flex align-center justify-center">
            <v-btn xlarge color="primary" height="54" @click="performRegistration">
              Register Account
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <!----------------------------------------------------------------------->
      <!-- Confirmation Message (with spinner) -->
      <!----------------------------------------------------------------------->
      <template v-else>
        <v-container v-if="isProcessing" class="d-flex flex-column align-center justify-center">
          <v-progress-circular indeterminate :size="50" style="margin:30px 0" color="primary" />
        </v-container>

        <div class="confirmation-message-area" v-else>
          <div class="main-text" style="font-size:1.2rem;">Thanks for signing-up with RouteLeaf!</div>
          <div class="mt-2 info-text" style="font-size:1.1rem;">Your account request has been submitted for approval.</div>
          <div class="mt-6">
            <div class="info-text" style="font-size:1.1rem;">An email has been sent to:</div>
            <div class="main-text">{{ registeredEmail }}</div>
          </div>
          <v-row align="center" class="mt-8 highlighted-info-container">
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-icon style="font-size:1.6rem;" color="yellow darken-3" class="pr-3">mdi-information-outline</v-icon>
              <div class="highlighted-info-text">You must confirm your email address to login.</div>
            </v-col>
          </v-row>
        </div>
      </template>

  </v-container>

</template>

<script>
import objectUtils from '@/utils/object-utils'

export default {
  name: 'RegisterShipperAccountPage',

  data () {
    return {
      requireRegistrationCode: false,
      isFormValid: true,
      showPassword: false,
      isProcessing: false,
      activeError: null,
      autoHideDelay: 6000, // ms
      registeredUser: null,
      // --------------- Input fields
      registrationCode: null,
      email: null,
      password: null,
      orgName: null,
      brandNames: null,
      firstName: null,
      lastName: null
    }
  },

  computed: {
    formTitleText () {
      return 'Register a Shipper Account'
    },
    registeredEmail () {
      return (this.registeredUser)
        ? objectUtils.get(this.registeredUser, 'data.attributes.email', this.email)
        : this.email
    },
    activeRegistrationError () {
      if (this.activeError) this.autoHideMessage()
      return this.activeError
    }
  },

  beforeDestroy () {
    this.resetForm()
  },

  methods: {
    // Auto-hide messages
    autoHideMessage () {
      if (this.autoHideDelay) {
        setTimeout(() => {
          this.activeError = null
        }, this.autoHideDelay)
      }
    },

    // Reset form
    resetForm () {
      this.registeredUser = null
      this.registrationCode = null
      this.email = null
      this.password = null
      this.orgName = null
      this.brandNames = null
      this.firstName = null
      this.lastName = null
    },

    // Perform registration
    async performRegistration () {
      await this.$refs.form.validate()
      if (!this.isFormValid) return false

      this.isProcessing = true

      try {
        const uri = 'auth/register-shipper-account'
        const input = {
          email: this.email,
          password: this.password,
          org_name: this.orgName,
          brand_names: this.brandNames,
          first_name: this.firstName,
          last_name: this.lastName
        }
        if (this.requireRegistrationCode) input.registration_code = this.registrationCode
        this.registeredUser = await this.$root.dataApi.post(uri, { bodyData: input })
      } catch (err) {
        this.activeError = err.message || err.text || err
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
  .page-register-shipper-account {
    margin-top: 75px;
    max-width: 480px;
  }

  .form-title {
    font-size: 1.5rem;
    color: #F8A824;
    font-weight: 400;
  }

  .main-text {
    font-size: 1.14rem;
    font-weight: 500;
  }

  .info-text {
    color: #d2d2d2;
  }

  .password-requirements-text {
    cursor: pointer;
    font-size: 1.0rem;
    color: #34b3db;
  }

  .registration-error-message-text {
    font-size: 15px;
    color: #cc0000;
    font-weight: 400;
  }

  .confirmation-message-area {
    text-align: center;
    max-width: 570px;
  }

  .highlighted-info-container {
    border: 1px solid #F9A825;
    border-radius: 6px;
  }

  .highlighted-info-text {
    font-size: 1.08rem;
    color: #F9A825;
    font-weight: 400;
  }
</style>
